import React from 'react';

import { Capability, hasAnyCapability } from '@repo/utils/auth';
import useCapabilities from '../../../utils/react/hooks/useCapabilities';
import useDocumentTitle from '../../../utils/react/hooks/useDocumentTitle';
import { NoPermissionMessage } from '../../PageLayout';
import { getPageTitle } from '../../PageLayout/utils';
import { Loading } from '../index.styles';

interface SettingsSectionProps {
  children: React.ReactNode;
  /** Names the browser tab while the section is open. */
  title: string;
  /** Opens to a caller holding any of these; omitted where every caller may open it. */
  authorisedCapabilities?: Capability[];
}

/**
 * One settings page inside the panel: its tab title, and the capabilities it is for.
 * The page behind the panel carries the layout.
 */
const SettingsSection = ({ children, title, authorisedCapabilities }: SettingsSectionProps) => {
  const capabilities = useCapabilities();

  useDocumentTitle(getPageTitle(title));

  // A gated section waits on the capabilities rather than opening to a reader
  // they may yet refuse; a list read and found empty is a refusal like any other
  if (authorisedCapabilities !== undefined && capabilities === undefined) {
    return <Loading />;
  }

  const isBarred =
    authorisedCapabilities !== undefined &&
    !hasAnyCapability(capabilities ?? [], authorisedCapabilities);

  return isBarred ? <NoPermissionMessage /> : <>{children}</>;
};

export default SettingsSection;
